import { useCallback, useEffect, useRef, useState } from 'react';
import type { AppLanguage } from '../i18n/types';
import {
  ensureMicrophoneAccess,
  nativeSpeechErrorCode,
  resolveSpeechBackend,
  startBrowserSpeechRecognition,
  startNativeSpeechRecognition,
  transcribeWithCloudStt,
  VOICE_LISTEN_MAX_MS,
  type BrowserSpeechSession,
} from './speechRecognition';

export type SpeechListenState = 'idle' | 'listening' | 'processing' | 'unsupported';

export function useSpeechRecognition(language: AppLanguage) {
  const [state, setState] = useState<SpeechListenState>(() =>
    resolveSpeechBackend() === 'unavailable' ? 'unsupported' : 'idle',
  );
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const sessionRef = useRef<BrowserSpeechSession | null>(null);
  const cloudAbortRef = useRef<AbortController | null>(null);
  const listenTimerRef = useRef<number | null>(null);
  const backend = resolveSpeechBackend();

  const clearListenTimer = useCallback(() => {
    if (listenTimerRef.current != null) {
      window.clearTimeout(listenTimerRef.current);
      listenTimerRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    clearListenTimer();
    sessionRef.current?.stop();
    sessionRef.current = null;
    cloudAbortRef.current?.abort();
    cloudAbortRef.current = null;
    setState((prev) => (prev === 'unsupported' ? prev : 'idle'));
  }, [clearListenTimer]);

  useEffect(() => () => stop(), [stop]);

  const start = useCallback(async () => {
    setErrorCode(null);
    setTranscript('');
    setInterimTranscript('');

    if (backend === 'unavailable') {
      setState('unsupported');
      setErrorCode('unavailable');
      return;
    }

    try {
      await ensureMicrophoneAccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'not-allowed';
      if (message === 'unavailable') {
        setErrorCode('unavailable');
        setState('unsupported');
        return;
      }
      setErrorCode(backend === 'native' ? nativeSpeechErrorCode(err) : 'not-allowed');
      setState('idle');
      return;
    }

    const armListenTimeout = () => {
      clearListenTimer();
      listenTimerRef.current = window.setTimeout(() => {
        listenTimerRef.current = null;
        sessionRef.current?.stop();
        sessionRef.current = null;
        cloudAbortRef.current?.abort();
        cloudAbortRef.current = null;
        setState((prev) => (prev === 'unsupported' ? prev : 'idle'));
      }, VOICE_LISTEN_MAX_MS);
    };

    const onResult = (payload: {
      transcript: string;
      interimTranscript: string;
      final: boolean;
    }) => {
      if (payload.final && payload.transcript) {
        setTranscript(payload.transcript);
        setInterimTranscript('');
      } else {
        setInterimTranscript(payload.interimTranscript || payload.transcript);
      }
    };
    const onError = (code: string) => {
      clearListenTimer();
      setErrorCode(code);
      setState('idle');
    };
    const onEnd = () => {
      clearListenTimer();
      sessionRef.current = null;
      setState('idle');
    };

    if (backend === 'native') {
      setState('listening');
      let session: BrowserSpeechSession | null = null;
      try {
        session = await startNativeSpeechRecognition(language, { onResult, onError, onEnd });
      } catch (err) {
        onError(nativeSpeechErrorCode(err));
        return;
      }
      if (!session) {
        setState('idle');
        return;
      }
      sessionRef.current = session;
      armListenTimeout();
      return;
    }

    if (backend === 'browser') {
      setState('listening');
      const session = startBrowserSpeechRecognition(language, { onResult, onError, onEnd });
      if (!session) {
        setErrorCode('start-failed');
        setState('idle');
        return;
      }
      sessionRef.current = session;
      armListenTimeout();
      return;
    }

    // Cloud STT path (Capacitor / browsers without Web Speech).
    setState('listening');
    const abort = new AbortController();
    cloudAbortRef.current = abort;
    armListenTimeout();
    try {
      const text = await transcribeWithCloudStt(language, {
        maxMs: VOICE_LISTEN_MAX_MS,
        signal: abort.signal,
      });
      setTranscript(text);
      setState('idle');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'cloud-failed';
      if (message !== 'aborted') {
        setErrorCode(message);
      }
      setState('idle');
    } finally {
      clearListenTimer();
      cloudAbortRef.current = null;
    }
  }, [backend, clearListenTimer, language]);

  const clearTranscript = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
    setErrorCode(null);
  }, []);

  return {
    backend,
    state,
    transcript,
    interimTranscript,
    errorCode,
    start,
    stop,
    setTranscript,
    clearTranscript,
  };
}
